import { supabase } from "@/integrations/supabase/client";

export type DocsBucket =
  | "scans"
  | "signatures"
  | "translated"
  | "verified"
  | "cvs"
  | "cover-letters";

export function ownerPath(userId: string, ...parts: string[]) {
  return [userId, ...parts.filter(Boolean)].join("/");
}

function extFromMime(mime: string): string {
  const map: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "application/pdf": "pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "text/plain": "txt",
    "application/json": "json",
  };
  return map[mime] ?? "bin";
}

export async function uploadOwnerFile(opts: {
  bucket: DocsBucket;
  userId: string;
  data: Blob | File | ArrayBuffer | Uint8Array;
  contentType: string;
  subdir?: string;
  fileName?: string;
}) {
  const { bucket, userId, data, contentType, subdir, fileName } = opts;
  const name =
    fileName ??
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extFromMime(contentType)}`;
  const path = ownerPath(userId, subdir ?? "", name).replace(/\/+/g, "/");
  let body: Blob;
  if (data instanceof Blob) body = data;
  else if (data instanceof ArrayBuffer) body = new Blob([data], { type: contentType });
  else body = new Blob([data.buffer as ArrayBuffer], { type: contentType });
  const { error } = await supabase.storage
    .from(bucket)
    .upload(path, body, { contentType, upsert: false });
  if (error) throw error;
  return { bucket, path, size: body.size };
}

export async function signedUrl(bucket: DocsBucket, path: string, expiresIn = 3600) {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
  if (error) throw error;
  return data.signedUrl;
}

export async function downloadOwnerFile(bucket: DocsBucket, path: string) {
  const { data, error } = await supabase.storage.from(bucket).download(path);
  if (error) throw error;
  return data;
}

export async function deleteOwnerFile(bucket: DocsBucket, path: string) {
  const { error } = await supabase.storage.from(bucket).remove([path]);
  if (error) throw error;
}

export async function logActivity(entry: {
  userId: string;
  action: string;
  tool: string;
  documentId?: string | null;
  fileName?: string | null;
  storageBucket?: string | null;
  storagePath?: string | null;
  sizeBytes?: number | null;
  metadata?: Record<string, unknown>;
}) {
  const { error } = await supabase.from("document_processing_history").insert({
    user_id: entry.userId,
    action: entry.action,
    tool: entry.tool,
    document_id: entry.documentId ?? null,
    file_name: entry.fileName ?? null,
    storage_bucket: entry.storageBucket ?? null,
    storage_path: entry.storagePath ?? null,
    size_bytes: entry.sizeBytes ?? null,
    metadata: (entry.metadata ?? {}) as never,
  });
  if (error) console.warn("logActivity failed", error);
}

export async function sha256Hex(data: ArrayBuffer | Uint8Array): Promise<string> {
  const buf =
    data instanceof Uint8Array
      ? (data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer)
      : data;
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
